import { Not, NumberGreaterThan, StringEquals, TimeAfter, TimeBefore } from '@vendasta/automata';
import { CurrencyInputDisplayService } from './dynamic-options/currency-input/currency-input-display.service';
import { CurrencyInputComponent } from './dynamic-options/currency-input/currency-input.component';
import { DateInputDisplayService } from './dynamic-options/date-input/date-input-display.service';
import { DateInputComponent } from './dynamic-options/date-input/date-input.component';
import { DataFilter } from './interface';
import { InvoiceStatusOptionsComponent } from './dynamic-options/invoice-status-options';
import { InvoiceStatusOptionsDisplayService } from './dynamic-options/invoice-status-options/invoice-status-options-display.service';
import {
  InvoiceOriginOptionsComponent,
  InvoiceOriginOptionsDisplayService,
} from './dynamic-options/invoice-origin-options';

const ISSUED_DATE = '{.issued}';

class IssuedDateDataFilter implements DataFilter {
  rules = [new TimeAfter(ISSUED_DATE), new TimeBefore(ISSUED_DATE)];
  dataEntryComponent = DateInputComponent;
  displayService = DateInputDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.INVOICE_DATA.DATA_LABELS.ISSUED_DATE';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.AFTER',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.BEFORE',
  ];
}

const DUE_DATE = '{.due}';

class DueDateDataFilter implements DataFilter {
  rules = [new TimeAfter(DUE_DATE), new TimeBefore(DUE_DATE)];
  dataEntryComponent = DateInputComponent;
  displayService = DateInputDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.INVOICE_DATA.DATA_LABELS.DUE_DATE';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.AFTER',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.BEFORE',
  ];
}

const PAID_DATE = '{.paid}';

class PaidDateDataFilter implements DataFilter {
  rules = [new TimeAfter(PAID_DATE), new TimeBefore(PAID_DATE)];
  dataEntryComponent = DateInputComponent;
  displayService = DateInputDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.INVOICE_DATA.DATA_LABELS.PAID_DATE';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.AFTER',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.BEFORE',
  ];
}

const VOIDED_DATE = '{.voided}';

class VoidedDataFilter implements DataFilter {
  rules = [new TimeAfter(VOIDED_DATE), new TimeBefore(VOIDED_DATE)];
  dataEntryComponent = DateInputComponent;
  displayService = DateInputDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.INVOICE_DATA.DATA_LABELS.VOIDED_DATE';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.AFTER',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.BEFORE',
  ];
}

const AMOUNT_OWING = '{.amount_owing}';

class AmountOwingDataFilter implements DataFilter {
  rules = [new Not(new NumberGreaterThan(AMOUNT_OWING)), new NumberGreaterThan(AMOUNT_OWING)];
  dataEntryComponentList = [CurrencyInputComponent, CurrencyInputComponent];
  displayService = CurrencyInputDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.INVOICE_DATA.DATA_LABELS.AMOUNT_OWING';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.LESS_THAN_EQUAL',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.GREATER_THAN',
  ];
}

const TOTAL = '{.total}';

class TotalDataFilter implements DataFilter {
  rules = [new Not(new NumberGreaterThan(TOTAL)), new NumberGreaterThan(TOTAL)];
  dataEntryComponentList = [CurrencyInputComponent, CurrencyInputComponent];
  displayService = CurrencyInputDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.INVOICE_DATA.DATA_LABELS.TOTAL';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.LESS_THAN_EQUAL',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.GREATER_THAN',
  ];
}

const AMOUNT_PAID = '{.amount_paid}';

class AmountPaidDataFilter implements DataFilter {
  rules = [new Not(new NumberGreaterThan(AMOUNT_PAID)), new NumberGreaterThan(AMOUNT_PAID)];
  dataEntryComponentList = [CurrencyInputComponent, CurrencyInputComponent];
  displayService = CurrencyInputDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.INVOICE_DATA.DATA_LABELS.AMOUNT_PAID';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.LESS_THAN_EQUAL',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.GREATER_THAN',
  ];
}

const STATUS = '{.status}';

class StatusDataFilter implements DataFilter {
  rules = [new StringEquals(STATUS), new Not(new StringEquals(STATUS))];
  dataEntryComponent = InvoiceStatusOptionsComponent;
  displayService = InvoiceStatusOptionsDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.DATA_LABELS.STATUS';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.EQUALS',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.NOT_EQUALS',
  ];
}

const ORIGIN = '{.origin}';

class OriginDataFilter implements DataFilter {
  rules = [new StringEquals(ORIGIN), new Not(new StringEquals(ORIGIN))];
  dataEntryComponent = InvoiceOriginOptionsComponent;
  displayService = InvoiceOriginOptionsDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.INVOICE_DATA.DATA_LABELS.ORIGIN.ORIGIN';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.EQUALS',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.NOT_EQUALS',
  ];
}

export const INVOICE_DATA_COMPONENTS: Map<string, DataFilter> = new Map<string, DataFilter>([
  [ISSUED_DATE, new IssuedDateDataFilter()],
  [DUE_DATE, new DueDateDataFilter()],
  [PAID_DATE, new PaidDateDataFilter()],
  [VOIDED_DATE, new VoidedDataFilter()],
  [AMOUNT_OWING, new AmountOwingDataFilter()],
  [TOTAL, new TotalDataFilter()],
  [AMOUNT_PAID, new AmountPaidDataFilter()],
  [STATUS, new StatusDataFilter()],
  [ORIGIN, new OriginDataFilter()],
]);
